'use client'

import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import type { AppRouter } from '@/trpc/routers/_app'
import {
  environmentManager,
  QueryClientProvider,
  type QueryClient,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createTRPCClient, httpBatchLink, loggerLink } from '@trpc/client'
import { createTRPCContext } from '@trpc/tanstack-react-query'
import SuperJSON from 'superjson'

import { makeQueryClient } from './query-client'

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>()

let browserQueryClient: QueryClient | undefined = undefined
let redirectToSignIn: (() => void) | undefined

function getQueryClient(): QueryClient {
  if (environmentManager.isServer()) {
    // Server: always make a new query client. Mutations don't run during
    // SSR, so there's no router to redirect with.
    return makeQueryClient()
  }

  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient(() => {
      redirectToSignIn?.()
    })
  }
  return browserQueryClient
}

function getUrl() {
  const base = (() => {
    if (typeof window !== 'undefined') return ''
    if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`
    return 'http://localhost:3000'
  })()
  return `${base}/api/trpc`
}

function isTransientClientFetchError(error: Error): boolean {
  const message = error.message || String(error)
  const cause = 'cause' in error ? error.cause : undefined
  const causeMessage = cause instanceof Error ? cause.message : String(cause)

  return (
    message.includes('Failed to fetch') ||
    message.includes('fetch failed') ||
    causeMessage.includes('Failed to fetch') ||
    causeMessage.includes('fetch failed')
  )
}

function isUnauthorizedScopeError(error: Error, path?: string): boolean {
  if (!path?.startsWith('assistant.scope.')) return false

  const data =
    'data' in error
      ? (error as { data?: { code?: string } }).data
      : undefined

  return data?.code === 'UNAUTHORIZED'
}

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const router = useRouter()
  const [queryClient] = useState(getQueryClient)

  useEffect(() => {
    redirectToSignIn = () => {
      router.push('/sign-in')
    }
    return () => {
      redirectToSignIn = undefined
    }
  }, [router])

  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        loggerLink({
          enabled: (op) => {
            // Only log real procedure errors, not transient dev-server fetches.
            if (op.direction !== 'down' || !(op.result instanceof Error)) {
              return false
            }

            if (isTransientClientFetchError(op.result)) {
              return false
            }

            if (isUnauthorizedScopeError(
              op.result,
              'path' in op ? String(op.path) : undefined,
            )) {
              return false
            }

            return true
          },
        }),
        httpBatchLink({
          url: getUrl(),
          headers: () => {
            const headers = new Headers()
            headers.set('x-trpc-source', 'nextjs-react')
            return headers
          },
          transformer: SuperJSON,
        }),
      ],
    }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {props.children}
      </TRPCProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
